import { useEffect, useState } from 'react'
import { formatCents } from '@core/money'
import { remainingCents, type Payable } from '@core/payments'
import { loadOpenRun, startRun, suggestForRun, type PaymentRun } from '@core/paymentRuns'
import type { Space } from '@core/session'
import { Button, Card, ErrorNote } from '@design/components'

/**
 * The offer to sit down and pay several things at once.
 *
 * Two states and no more: there is a run in progress, or there is a sensible
 * one to start. If neither, the banner is not there at all (rule 23) — an
 * empty "0 payments ready" row is worse than silence.
 *
 * It says what the run contains and what it totals before you commit, because
 * the decision being made is "do I have twenty minutes for this now", and that
 * needs a number.
 */
export function RunBanner({
  space,
  payables,
  onStarted,
  onResume,
}: {
  space: Space
  payables: Payable[]
  onStarted: () => void
  onResume: () => void
}) {
  const [existing, setExisting] = useState<PaymentRun | null | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    loadOpenRun(space.id)
      .then(setExisting)
      .catch(() => setExisting(null))
  }, [space.id])

  const candidates = suggestForRun(payables, space.id)
  const total = candidates.reduce((sum, p) => sum + remainingCents(p), 0)

  if (existing === undefined) return null

  if (existing) {
    const left = existing.items.filter((i) => i.state === 'pending').length
    if (left === 0) return null
    return (
      <Card className="mb-4 border-ink/20">
        <p className="font-medium mb-1">Betaalronde loopt · {space.name}</p>
        <p className="text-sm text-muted mb-3">
          Nog {left} {left === 1 ? 'betaling' : 'betalingen'} te gaan.
        </p>
        <Button onClick={onResume}>Verder gaan</Button>
      </Card>
    )
  }

  // Below two, a run is more ceremony than just paying the thing.
  if (candidates.length < 2) return null

  return (
    <Card className="mb-4">
      <p className="font-medium mb-1">Samen betalen · {space.name}</p>
      <p className="text-sm text-muted mb-3">
        {candidates.length} betalingen, samen {formatCents(total)}.
      </p>
      <ErrorNote>{error}</ErrorNote>
      <Button
        disabled={busy}
        onClick={async () => {
          setBusy(true)
          setError('')
          try {
            await startRun(
              space.id,
              candidates.map((c) => c.instalmentId),
              space.name,
            )
            onStarted()
          } catch (e) {
            // The likeliest cause is rule 15 doing its job: something in the
            // list was never verified. Say so plainly rather than "error".
            const message = e instanceof Error ? e.message : ''
            setError(
              message.includes('unverified')
                ? 'Er staat iets tussen dat nog niet nagekeken is. Kijk dat eerst na.'
                : 'Starten lukte niet.',
            )
          }
          setBusy(false)
        }}
      >
        {busy ? 'Bezig…' : 'Betaalronde starten'}
      </Button>
    </Card>
  )
}
